import { Formik, Form } from "formik";
import Button from "../components/common/Button";
import { useFetchAllCitiesQuery } from "../store";
import ImageUploader from "../components/common/UploadImage";
import classNames from "classnames";
import { ReactNode } from "react";
import FormikFieldArray from "../components/common/FormikFieldArray";

interface AdFormProps {
  initialValues: {
    cities: string[];
    image?: File;
    previewPath?: string;
  };
  validationSchema: any;
  isLoading?: boolean;
  onSubmit: (
    values: { cities: string[]; image?: File },
    helpers: { resetForm: () => void }
  ) => Promise<void>;
}

const sectionTitleClasses = "mr-auto mt-4 text-primary font-semibold";

const AdForm: React.FC<AdFormProps> = ({
  initialValues,
  validationSchema,
  isLoading,
  onSubmit,
}) => {
  const { data: cities } = useFetchAllCitiesQuery();

  const errorMessageClasses = classNames(
    "w-full text-sm text-red-700 border-b border-red-700 rounded"
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
      }) => (
        <Form className="mx-auto w-3/5 flex flex-col items-center mt-12 gap-3">
          {/* Image upload */}
          <ImageUploader
            label="Image de la publicité"
            imgUrl={initialValues.previewPath}
            onUpload={(file) => {
              void setFieldValue("image", file);
              void setFieldTouched("image", true);
            }}
          />

          {/* Image validation error */}
          {errors.image && touched.image && (
            <div className={errorMessageClasses}>
              {errors.image as ReactNode}
            </div>
          )}

          {/* Cities selection */}
          <h3 className={sectionTitleClasses}>Villes concernées</h3>

          <FormikFieldArray
            name="cities"
            label="Liste des villes"
            pushItem="" // Value for new item if using default button, but we use custom add button
            renderItem={(cityId: string) => {
              const city = cities?.find((c) => c._id === cityId);
              return (
                <div className="flex justify-between items-center w-full">
                  <span className="font-medium text-gray-700">
                    {city?.displayName || "Ville inconnue"}
                  </span>
                </div>
              );
            }}
            renderAddButton={(push) => (
              <select
                className="w-full border rounded p-2 mt-2 bg-white"
                defaultValue=""
                onChange={(e) => {
                  push(e.target.value);
                  e.target.value = "";
                }}
              >
                <option value="" disabled>
                  Ajouter une ville
                </option>
                {cities
                  ?.filter((city) => !values.cities.includes(city._id))
                  .map((city) => (
                    <option key={city._id} value={city._id}>
                      {city.displayName}
                    </option>
                  ))}
              </select>
            )}
          />

          {/* Submit */}
          <Button
            type="submit"
            disabled={isSubmitting || isLoading}
            className="w-1/2 justify-center font-bold py-2 bg-primary rounded text-white mt-4"
          >
            Enregistrer
          </Button>
        </Form>
      )}
    </Formik>
  );
};

export default AdForm;
